import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, AlertCircle, LogOut, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePermissions } from '../context/PermissionContext';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';
import { GENERATE_AUTH_TOKEN } from '../config';

const countries = [
  'Argentina',
  'Bolivia',
  'Brasil',
  'Chile',
  'Colombia',
  'Ecuador',
  'Paraguay',
  'Perú',
  'Uruguay',
  'Venezuela',
  'México',
  'Estados Unidos',
  'España',
  'Otro'
];

const rubroOptions = [
  'Moda e Indumentaria',
  'Hogar y Decoración',
  'Belleza y Cuidado Personal',
  'Tecnología',
  'Salud y Bienestar',
  'Automotriz',
  'Gastronomía',
  'Juguetería',
  'Arte y Manualidades',
  'Librería',
  'Creación de Contenido',
  'Otro',
];

/* ── Custom Country Select (no native OS dropdown) ── */
const CountrySelect = ({ value, onChange, required }: { value: string; onChange: (v: string) => void; required?: boolean }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
          value={value}
          onChange={() => {}}
          required
        />
      )}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className={value ? 'text-brand-black/90' : 'text-neutral-400'}>{value || 'Selecciona tu país'}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto">
          {countries.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => { onChange(country); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                value === country ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span>{country}</span>
              {value === country && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Custom Rubro Select (no native OS dropdown) ── */
const RubroSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] bg-surface-card hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
      >
        <span className="text-brand-black/90">{value}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 max-h-60 overflow-y-auto">
          {rubroOptions.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                onChange(r);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-[15px] transition-colors flex items-center justify-between ${
                value === r ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span>{r}</span>
              {value === r && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ServerResponse {
  response: 'success' | 'error';
  description: string;
}

export const Onboarding = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    pais: '',
    company_name: '',
    industry: 'Moda e Indumentaria',
  });
  const [industryOption, setIndustryOption] = useState('Moda e Indumentaria');
  const [customIndustry, setCustomIndustry] = useState('');

  // Redirect away if user already has a company (prevent duplicate creation on back navigation)
  useEffect(() => {
    const checkCompany = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if ((profile as any)?.id_company) {
          navigate('/add-first-product', { replace: true });
        }
      } catch {}
    };
    checkCompany();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate
    if (!formData.name.trim()) { setError('El nombre es obligatorio'); setLoading(false); return; }
    if (!formData.pais) { setError('Seleccioná un país'); setLoading(false); return; }
    if (!formData.company_name.trim()) { setError('El nombre de la marca es obligatorio'); setLoading(false); return; }
    if (!formData.industry.trim()) { setError('El rubro es obligatorio'); setLoading(false); return; }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // 1) Save name + pais to profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ name: formData.name.trim(), pais: formData.pais })
        .eq('id', user.id);
      if (profileError) throw profileError;

      // 2) Call API to create company (sends name + pais too)
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/analizar_nombre_y_logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          company_name: formData.company_name.trim(),
          industry: formData.industry.trim(),
          user_id: user.id,
          name: formData.name.trim(),
          pais: formData.pais,
        }),
      });

      const data: ServerResponse = await response.json();

      if (data.response === 'error') {
        throw new Error(data.description);
      }

      if (data.response === 'success') {
        // 3) Refresh permissions
        await refreshPermissions();

        // 4) Save rubro to company_profiles
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company')
            .eq('id', user.id)
            .single();
          const companyId = (profile as any)?.id_company as string | null;
          if (companyId) {
            await supabase
              .from('company_profiles')
              .update({ rubro: formData.industry.trim() })
              .eq('id_company', companyId);
            mergeLastCrearAnuncioState({ rubro: formData.industry.trim() });
          }
        } catch {}

        // 5) Navigate with replace to prevent going back
        navigate('/add-first-product', { replace: true });
      }
    } catch (err) {
      console.error('Error during onboarding:', err);
      setError(err instanceof Error ? err.message : 'Error al crear la cuenta');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white md:bg-surface-bg flex items-center justify-center p-4 md:bg-cover md:bg-center md:bg-no-repeat"
      style={{ backgroundImage: 'var(--onboarding-bg)' }}
    >
      <style>{`@media (min-width: 768px) { :root { --onboarding-bg: url('https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/fondo_onboarding_compu.jpg'); } }`}</style>
      <div className="w-full max-w-md md:max-w-xl">
        <div className="bg-white md:bg-surface-card rounded-none md:rounded-lg shadow-none md:shadow-sm px-6 md:p-8">
          <p className="text-[13px] font-medium text-neutral-400 mb-1.5">
            Bienvenido a Atlas
          </p>
          <h2 className="text-[30px] md:text-[22px] font-semibold text-brand-black/90 mb-8 leading-tight">
            Vamos a conocernos
          </h2>

          {error && (
            <div className="mb-5 p-3 bg-error-soft border border-error/20 rounded-md text-[13px] text-error-text flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="text-[13px] font-medium text-neutral-600 mb-1 block">
                Nombre y apellido
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                placeholder="Tu nombre"
                required
              />
            </div>

            <div>
              <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                País
              </label>
              <CountrySelect
                value={formData.pais}
                onChange={(v) => setFormData({ ...formData, pais: v })}
                required
              />
            </div>

            <div>
              <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                Nombre de la marca
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                placeholder="Nombre de tu marca o negocio"
                required
              />
            </div>

            <div>
              <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
                Rubro
              </label>
              <div className="flex flex-col md:flex-row gap-3">
                <div className={industryOption === 'Otro' ? 'md:w-1/2' : 'w-full'}>
                  <RubroSelect
                    value={industryOption}
                    onChange={(r) => {
                      setIndustryOption(r);
                      if (r !== 'Otro') {
                        setCustomIndustry('');
                        setFormData(prev => ({ ...prev, industry: r }));
                      } else {
                        setFormData(prev => ({ ...prev, industry: customIndustry }));
                      }
                    }}
                  />
                </div>
                {industryOption === 'Otro' && (
                  <input
                    type="text"
                    value={customIndustry}
                    onChange={(e) => {
                      setCustomIndustry(e.target.value);
                      setFormData(prev => ({ ...prev, industry: e.target.value }));
                    }}
                    placeholder="Especificá tu rubro"
                    className="w-full md:w-1/2 px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                    required
                  />
                )}
              </div>
            </div>

            {loading && (
              <div className="p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md text-[13px] text-brand-primary flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <p>Creando tu cuenta. Este proceso puede tomar unos segundos</p>
              </div>
            )}

            <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full md:w-auto h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Continuar'
                )}
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="w-full md:w-auto h-[var(--btn-h)] px-5 inline-flex items-center justify-center gap-2 text-neutral-600 hover:bg-neutral-100 rounded-md font-medium text-[15px] transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};